import type { ReactElement } from "react";
import React from "react";

import _ from "lodash";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";

import { ComponentsUtils } from "../../../ComponentsUtils";

import { AutoCompletePopup } from "./AutoCompletePopup";
import * as ItemComponents from "./Stateless";
import type { IAutoCompleteItemProps, IAutoCompleteProps, IAutoCompleteState } from "./types";

export class AutoComplete extends React.Component<IAutoCompleteProps, IAutoCompleteState> {
    static defaultProps = {
        itemComponent: ItemComponents.SimpleItem,
        emphasisComponent: ItemComponents.StrongEmphasisComponent,
        debounce: 300,
        minSize: 2,
        popupClass: "",
        hasKeywordGroupsInSuggestions: true,
    };

    // @ts-ignore FIXME: strict migration - TS2564 class field assigned in showPopup; legacy class lifecycle
    private _autocompleteRoot: Root;
    private _domRoot: HTMLDivElement;
    // @ts-ignore FIXME: strict migration - TS2564 class field assigned in ref callback; legacy class lifecycle
    public inputElem: HTMLInputElement;

    constructor(props: IAutoCompleteProps) {
        super(props);
        this.state = { selectedItem: 1, data: [] };
        this._domRoot = ComponentsUtils.createDomRoot("_autocompleteRootID");
    }

    moveSelectedItem(up: boolean) {
        return up
            ? this.state.selectedItem === 0
                ? this.state.data.length
                : this.state.selectedItem - 1
            : this.state.selectedItem === this.state.data.length
              ? 0
              : this.state.selectedItem + 1;
    }

    onKeyUp(event: React.KeyboardEvent<any>) {
        switch (event.key) {
            case "ArrowDown":
                this.setState({
                    selectedItem: this.moveSelectedItem(false),
                    data: this.state.data,
                });
                event.preventDefault();
                return;
            case "ArrowUp":
                this.setState({ selectedItem: this.moveSelectedItem(true), data: this.state.data });
                event.preventDefault();
                return;
            case "Enter":
                const selectedValue = _.trim(this.state.data[this.state.selectedItem - 1] || this.inputElem.value);
                if (selectedValue) {
                    this.onSelect({ value: selectedValue });
                }
                event.preventDefault();
                return;
            case "Escape":
                this.setState({ selectedItem: 1, data: [] });
                return;
        }

        const value = this.inputElem.value;
        // @ts-ignore FIXME: strict migration - minSize is optional but defaultProps sets it to 2
        if (!value || value.length < this.props.minSize) {
            return;
        }
        this.getSuggestions();
    }

    getSuggestions = _.debounce(() => {
        this.props.suggestionProvider
            ?.getSuggestions(this.inputElem.value, this.props.hasKeywordGroupsInSuggestions)
            .then((res) => {
                if (res.query && res.query !== this.inputElem.value) {
                    return;
                }
                this.setState({ data: res, selectedItem: 1 });
            });
    }, this.props.debounce);

    onBlur(event: React.FocusEvent<any>) {
        // blur event does not always provide relatedTarget
        const origin = (event.relatedTarget || document.activeElement) as HTMLElement;
        if (origin && origin.classList.contains("js-dropdown-menu-item")) {
            return;
        }
        this.setState({ selectedItem: 1, data: [] });
    }

    onSelect = (item: IAutoCompleteItemProps | { value: string }) => {
        // this.inputElem.value = item.value;
        // this.inputElem.focus();
        this.setState({ selectedItem: 1, data: [] });
        this.props.onSelect(item);
    };

    componentDidUpdate() {
        if (this.state.data.length && this.inputElem.value) {
            this.showPopup();
        } else {
            this.hidePopup();
        }
    }

    showPopup() {
        const input = this.inputElem;
        const inputSize = input.getBoundingClientRect();
        const popupWidth = this.props.width || inputSize.width;

        const popup = (
            <AutoCompletePopup
                items={this.state.data}
                query={input.value}
                // @ts-ignore FIXME: strict migration - itemComponent optional in props; defaultProps populates it
                itemComponent={this.props.itemComponent}
                // @ts-ignore FIXME: strict migration - emphasisComponent optional in props; defaultProps populates it
                emphasisComponent={this.props.emphasisComponent}
                // @ts-ignore FIXME: strict migration - popupClass optional in props; defaultProps populates it
                popupClass={this.props.popupClass}
                selectedItem={this.state.selectedItem}
                onSelect={this.onSelect}
                width={popupWidth}
                inputElem={input}
            />
        );
        this._autocompleteRoot = createRoot(this._domRoot);
        this._autocompleteRoot.render(popup);
    }

    hidePopup() {
        if (this._autocompleteRoot) {
            this._autocompleteRoot.unmount();
        }
    }

    componentWillUnmount() {
        this.hidePopup();
        ComponentsUtils.deleteDomRoot(this._domRoot);
    }

    render() {
        // attach listeners
        const inputElem: ReactElement = React.Children.only(this.props.children as ReactElement);

        return this.extendComponent(inputElem, {
            onKeyUp: this.onKeyUp,
            onBlur: this.onBlur,
            ref: (e: FixmeType) => (this.inputElem = e),
        });
    }

    private extendComponent(component: FixmeType, props: FixmeType) {
        const newProps: FixmeType = {};
        _.forEach(props, (newProp, prop) => {
            const origProp = component.props[prop];
            if (_.isFunction(newProp)) {
                newProps[prop] = function (this: FixmeType) {
                    // eslint-disable-next-line prefer-rest-params
                    const args = Array.prototype.slice.call(arguments);
                    origProp && origProp.apply(component, args);
                    newProp.apply(this, args);
                }.bind(this);
            } else {
                newProps[prop] = newProp;
            }
        });
        return React.cloneElement(component, newProps);
    }
}
